import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Markdown } from './Markdown.js';

interface Props {
  toolName: string;
  toolInput: string;
}

export function ToolInputView({ toolName, toolInput }: Props) {
  const input = safeParse(toolInput);
  return <Renderer toolName={toolName} input={input} />;
}

function Renderer({ toolName, input }: { toolName: string; input: unknown }) {
  if (!isRecord(input)) return <JsonFallback value={input} />;

  switch (toolName) {
    case 'ExitPlanMode':
      return <ExitPlanModeView plan={asString(input.plan)} />;
    case 'Bash':
      return (
        <BashView
          command={asString(input.command)}
          description={asString(input.description)}
          timeout={asNumber(input.timeout)}
        />
      );
    case 'Edit':
    case 'Write':
      return (
        <EditView
          filePath={asString(input.file_path)}
          oldString={asString(input.old_string)}
          newString={asString(input.new_string ?? input.content)}
        />
      );
    case 'Read':
      return (
        <ReadView
          filePath={asString(input.file_path)}
          offset={asNumber(input.offset)}
          limit={asNumber(input.limit)}
        />
      );
    case 'Glob':
    case 'Grep':
      return <SearchView input={input} />;
    case 'AskUserQuestion':
      return <AskUserQuestionView questions={input.questions} />;
    default:
      return <JsonFallback value={input} />;
  }
}

interface AskQuestion {
  question: string;
  header?: string;
  options?: Array<{ label: string; description?: string }>;
  multiSelect?: boolean;
}

function AskUserQuestionView({ questions }: { questions: unknown }) {
  if (!Array.isArray(questions) || questions.length === 0) {
    return <JsonFallback value={{ questions }} />;
  }
  const parsed = questions
    .map(parseQuestion)
    .filter((q): q is AskQuestion => q !== null);
  if (parsed.length === 0) return <JsonFallback value={{ questions }} />;
  return (
    <div className="space-y-3">
      {parsed.map((q, i) => (
        <div key={i} className="rounded-lg border border-amber-500/30 bg-amber-950/10 p-3 space-y-2">
          {q.header && (
            <div className="text-[11px] uppercase tracking-wide text-amber-300/70">{q.header}</div>
          )}
          <div className="text-[14px] font-medium text-amber-50">{q.question}</div>
          {q.options && q.options.length > 0 && (
            <ul className="space-y-1.5 mt-1">
              {q.options.map((opt, j) => (
                <li
                  key={j}
                  className="rounded-md border border-amber-500/15 bg-black/20 p-2"
                >
                  <div className="text-[13px] font-medium text-amber-100">
                    {q.multiSelect ? '☐' : '○'} {opt.label}
                  </div>
                  {opt.description && (
                    <div className="text-[12px] text-amber-100/70 mt-0.5 leading-snug">
                      {opt.description}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
      <div className="text-[11px] text-amber-300/60 italic">
        Approve, чтобы агент задал эти вопросы (ответы пока через текстовый промпт).
      </div>
    </div>
  );
}

function parseQuestion(raw: unknown): AskQuestion | null {
  if (!isRecord(raw)) return null;
  const question = asString(raw.question);
  if (!question) return null;
  const options = Array.isArray(raw.options)
    ? raw.options
        .map((o) => {
          if (!isRecord(o)) return null;
          const label = asString(o.label);
          if (!label) return null;
          const description = asString(o.description);
          return description ? { label, description } : { label };
        })
        .filter((o): o is { label: string; description?: string } => o !== null)
    : [];
  const result: AskQuestion = { question, options };
  const header = asString(raw.header);
  if (header) result.header = header;
  if (typeof raw.multiSelect === 'boolean') result.multiSelect = raw.multiSelect;
  return result;
}

function ExitPlanModeView({ plan }: { plan: string | null }) {
  if (!plan) return <JsonFallback value={{ plan }} />;
  return (
    <Collapsible label="План">
      <Markdown tone="amber">{plan}</Markdown>
    </Collapsible>
  );
}

function BashView({
  command,
  description,
  timeout,
}: {
  command: string | null;
  description: string | null;
  timeout: number | null;
}) {
  return (
    <div className="space-y-2">
      {description && <div className="text-sm text-amber-100/90">{description}</div>}
      <pre className="text-xs font-mono text-amber-50 bg-black/40 rounded-lg p-2.5 whitespace-pre-wrap break-words max-h-48 overflow-auto">
        {command ?? ''}
      </pre>
      {timeout !== null && (
        <div className="text-[11px] text-neutral-400">timeout: {timeout} мс</div>
      )}
    </div>
  );
}

function EditView({
  filePath,
  oldString,
  newString,
}: {
  filePath: string | null;
  oldString: string | null;
  newString: string | null;
}) {
  return (
    <div className="space-y-2">
      {filePath && (
        <div className="text-xs font-mono text-amber-200 break-all">{filePath}</div>
      )}
      {oldString !== null && (
        <DiffBlock label="− было" tone="red" content={oldString} />
      )}
      {newString !== null && (
        <DiffBlock label="+ станет" tone="green" content={newString} />
      )}
    </div>
  );
}

function DiffBlock({
  label,
  tone,
  content,
}: {
  label: string;
  tone: 'red' | 'green';
  content: string;
}) {
  const colors =
    tone === 'red'
      ? 'border-red-500/30 bg-red-950/30 text-red-100'
      : 'border-emerald-500/30 bg-emerald-950/30 text-emerald-100';
  return (
    <div className={`rounded-lg border ${colors}`}>
      <div className="text-[11px] px-2 pt-1.5 opacity-70">{label}</div>
      <pre className="text-xs font-mono px-2 pb-2 pt-1 whitespace-pre-wrap break-words max-h-48 overflow-auto">
        {content}
      </pre>
    </div>
  );
}

function ReadView({
  filePath,
  offset,
  limit,
}: {
  filePath: string | null;
  offset: number | null;
  limit: number | null;
}) {
  return (
    <div className="text-sm space-y-1">
      <div className="font-mono text-amber-200 break-all">{filePath ?? '(без пути)'}</div>
      {(offset !== null || limit !== null) && (
        <div className="text-[11px] text-neutral-400">
          {offset !== null && <>смещение: {offset}</>}
          {offset !== null && limit !== null && <> · </>}
          {limit !== null && <>строк: {limit}</>}
        </div>
      )}
    </div>
  );
}

function SearchView({ input }: { input: Record<string, unknown> }) {
  const pattern = asString(input.pattern);
  const path = asString(input.path);
  const glob = asString(input.glob);
  return (
    <div className="text-sm space-y-1">
      {pattern && (
        <div>
          <span className="text-neutral-400 text-[11px]">pattern: </span>
          <span className="font-mono text-amber-200">{pattern}</span>
        </div>
      )}
      {path && (
        <div className="text-xs">
          <span className="text-neutral-400">в </span>
          <span className="font-mono text-amber-200/80 break-all">{path}</span>
        </div>
      )}
      {glob && (
        <div className="text-xs">
          <span className="text-neutral-400">glob </span>
          <span className="font-mono text-amber-200/80">{glob}</span>
        </div>
      )}
    </div>
  );
}

function JsonFallback({ value }: { value: unknown }) {
  return (
    <Collapsible label="Параметры">
      <pre className="text-xs font-mono text-amber-100/90 whitespace-pre-wrap break-words">
        {JSON.stringify(value, null, 2)}
      </pre>
    </Collapsible>
  );
}

function Collapsible({ label, children }: { label: string; children: React.ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="space-y-1.5">
      <div
        className={
          expanded
            ? 'max-h-[60vh] overflow-auto'
            : 'max-h-32 overflow-hidden relative'
        }
      >
        {children}
        {!expanded && (
          <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-neutral-900 to-transparent pointer-events-none" />
        )}
      </div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-[11px] text-amber-300/80 hover:text-amber-200 inline-flex items-center gap-1"
      >
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {expanded ? 'Свернуть' : `${label} целиком`}
      </button>
    </div>
  );
}

function safeParse(json: string): unknown {
  try {
    return JSON.parse(json);
  } catch {
    return json;
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
